import {toCamelCase} from 'case-converter'
import {
  assignPrices,
  checkPricesWarnings,
  fetchErrorsHandler, fetchStart, fetchSuccessful,
  fetchSynchronizer,
  filterByValues, getFetchDate
} from '~/helpers/prices/_utils/prices'
import {additionalPrices} from '~/helpers/prices/primary/_additional-prices'
import {clonePrices} from '~/helpers/prices/primary/_clone-prices'
import {addAdditionalGroupForVols, mapPricesData} from '~/helpers/prices/primary/utils'
import {AdditionalGroup, Group, Status, SubType} from '~/helpers/prices/primary/enum'

export const state = () => ({
  pricesPrimary: [],
  primaryFetchDate: '',
  fetchingNow: false,
  fetchingResult: ''
})

export const mutations = {
  setPrimaryPrices(state, value) {
    state.pricesPrimary = value
  },
  setPrimaryFetchDate(state, value) {
    state.primaryFetchDate = value
  },
  setFetchingState(state, value) {
    state.fetchingNow = value
  },
  setFetchingResult(state, value) {
    state.fetchingResult = value
  }
}

export const actions = {
  async fetchPrimaryPrices({commit, state}, options) {
    if (state.fetchingNow) {
      return fetchSynchronizer(commit, state)
    } else {
      fetchStart(commit)

      return this.$http.$get('https://api.selectel.ru/v2/billing/prices', options)
        .then(res => toCamelCase(res))
        .then(prices => [...prices, ...additionalPrices])
        .then(prices => assignPrices(prices, clonePrices, ['id']))
        .then(prices => mapPricesData(prices))
        .then(prices => checkPricesWarnings(prices))
        .then(prices => addAdditionalGroupForVols(prices))
        .then(prices => {
          commit('setPrimaryPrices', prices)

          commit('setPrimaryFetchDate', getFetchDate())

          fetchSuccessful(commit)

          return prices
        })
        .catch(err => fetchErrorsHandler(commit, err))
    }
  }
}

export const getters = {
  getPrimaryFetchDate(state) {
    return state.primaryFetchDate
  },
  getPrimaryPrices(state) {
    return state.pricesPrimary
  },
  getPrimaryGoogleWorkspaceTablePrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'additionalGroup',
          values: [AdditionalGroup.GSUITE]
        }
      ]
    )
  },
  getPrimaryDdosProtectionTablePrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'additionalGroup',
          values: [AdditionalGroup.QRATOR]
        }
      ]
    )
  },
  getPrimaryVrrpPrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'id',
          values: [289]
        }
      ]
    )
  },
  getPrimaryVeeamCloudConnectPrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'additionalGroup',
          values: [AdditionalGroup.VEEAM_CLOUD_CONNECT]
        }
      ]
    )
  },
  getPrimaryServerRackPrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'group',
          values: [Group.RACKS]
        }
      ]
    )
  },
  getPrimaryRentVolsPrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'additionalGroup',
          values: [AdditionalGroup.VOLS_SPB, AdditionalGroup.VOLS_MSK]
        }
      ]
    )
  },
  getPrimaryActivePrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        }
      ]
    )
  },
  getPrimaryRentSoftPrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'group',
          values: [Group.CUSTOM]
        }
      ]
    )
  },
  getPrimaryNetworkEquipmentPrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'group',
          values: [Group.NETWORK_EQUIPMENT]
        }
      ]
    )
  },
  getPrimaryMonitoringPrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'group',
          values: [Group.MONITORING, Group.NOTIFICATION]
        }
      ]
    )
  },
  getPrimaryFirewallPrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'additionalGroup',
          values: [AdditionalGroup.FIREWALL]
        }
      ]
    )
  },
  getPrimaryDdosProtectionPrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'additionalGroup',
          values: [AdditionalGroup.DDOS_PROTECTION]
        }
      ]
    )
  },
  getPrimaryColocationPrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'group',
          values: [Group.COLOCATION]
        }
      ]
    )
  },
  getPrimaryBackupCopyPrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'additionalGroup',
          values: [AdditionalGroup.VEEAM_CLOUD_CONNECT, AdditionalGroup.AGENT]
        }
      ]
    )
  },
  getPrimaryAgentPrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'additionalGroup',
          values: [AdditionalGroup.AGENT]
        }
      ]
    )
  },
  getPrimaryAdministrativePrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'group',
          values: [Group.ADMINISTRATING]
        }
      ]
    )
  },
  getPrimaryAdditionalServicesPrices(state) {
    return filterByValues(
      state.pricesPrimary,
      [
        {
          key: 'status',
          values: [Status.ACTIVE]
        },
        {
          key: 'group',
          values: [Group.DEDICATED]
        },
        {
          key: 'subtype',
          values: [SubType.ADDITIONAL]
        }
      ]
    )
  }
}